import { useEffect, useState } from "react";
import { Mail, Plus, Play, Pause, Trash2, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const STATUS_COLORS = {
  draft: "bg-[#F4F4EE] text-[#5C5C66]",
  active: "bg-green-50 text-[#16A34A]",
  paused: "bg-amber-50 text-[#F59E0B]",
};

const TONES = ["professional", "casual", "bold", "friendly"];

export default function Sequences() {
  const [campaigns, setCampaigns] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({
    name: "",
    campaign_id: "",
    offer: "",
    steps: [{ delay_days: 0, tone: "professional", offer: "", subject_hint: "" }],
  });

  useEffect(() => {
    api.get("/campaigns").then((r) => {
      setCampaigns(r.data);
      if (r.data.length > 0) setSelectedCampaign(r.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedCampaign) return;
    api.get(`/sequences/campaign/${selectedCampaign}`).then((r) => setSequences(r.data)).catch(() => {});
  }, [selectedCampaign]);

  const addStep = () =>
    setForm((f) => ({ ...f, steps: [...f.steps, { delay_days: f.steps.length * 3, tone: "professional", offer: "", subject_hint: "" }] }));

  const updateStep = (i, key, val) =>
    setForm((f) => { const s = [...f.steps]; s[i] = { ...s[i], [key]: val }; return { ...f, steps: s }; });

  const removeStep = (i) =>
    setForm((f) => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));

  const handleCreate = async () => {
    if (!form.name || !form.campaign_id) { toast.error("Name and campaign are required"); return; }
    setLoading(true);
    try {
      await api.post("/sequences", form);
      toast.success("Sequence created");
      setOpen(false);
      setForm({ name: "", campaign_id: selectedCampaign, offer: "", steps: [{ delay_days: 0, tone: "professional", offer: "", subject_hint: "" }] });
      api.get(`/sequences/campaign/${selectedCampaign}`).then((r) => setSequences(r.data));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create sequence");
    } finally { setLoading(false); }
  };

  const activate = async (id) => {
    try {
      const r = await api.post(`/sequences/${id}/activate`);
      toast.success(`Activated — ${r.data.generated} emails drafted`);
      api.get(`/sequences/campaign/${selectedCampaign}`).then((r) => setSequences(r.data));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to activate"); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/sequences/${id}/status`, { status });
      setSequences((prev) => prev.map((s) => s.id === id ? { ...s, status } : s));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteSeq = async (id) => {
    if (!window.confirm("Delete this sequence?")) return;
    try {
      await api.delete(`/sequences/${id}`);
      setSequences((prev) => prev.filter((s) => s.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-mono mb-2">— Automation</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Drip Sequences</h1>
          <p className="text-[#5C5C66] mt-2">Multi-step outreach sequences — set it once, reach every lead automatically.</p>
        </div>
        <Button onClick={() => { setForm((f) => ({ ...f, campaign_id: selectedCampaign })); setOpen(true); }}
          className="rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white">
          <Plus className="h-4 w-4 mr-2" /> New sequence
        </Button>
      </div>

      {/* Campaign selector */}
      <div className="mb-6">
        <Label className="label-mono mb-2 block">Campaign</Label>
        <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
          <SelectTrigger className="w-80 rounded-lg border-[#EAEAE3]">
            <SelectValue placeholder="Select a campaign" />
          </SelectTrigger>
          <SelectContent>
            {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {sequences.length === 0 ? (
        <div className="bg-white border border-[#EAEAE3] rounded-2xl p-16 text-center shadow-soft">
          <div className="w-14 h-14 rounded-2xl bg-[#F4F4EE] flex items-center justify-center mx-auto mb-4">
            <Mail className="h-7 w-7 text-[#9A9AA3]" />
          </div>
          <div className="font-display text-xl font-bold tracking-tighter mb-2">No sequences yet</div>
          <p className="text-[#5C5C66] text-sm mb-6">Create a drip sequence to automatically follow up with every lead in a campaign.</p>
          <Button onClick={() => setOpen(true)} className="rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white">
            <Plus className="h-4 w-4 mr-2" /> Create your first sequence
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {sequences.map((seq) => (
            <div key={seq.id} className="bg-white border border-[#EAEAE3] rounded-2xl shadow-soft overflow-hidden">
              <div className="p-5 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center shrink-0">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-lg font-bold tracking-tighter truncate">{seq.name}</div>
                    <div className="text-xs text-[#5C5C66] font-mono">{seq.steps.length} step{seq.steps.length !== 1 ? "s" : ""}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-md font-mono text-[10px] uppercase tracking-wider ${STATUS_COLORS[seq.status] || ""}`}>
                    {seq.status}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="rounded-lg h-8 w-8"
                    onClick={() => setExpanded(expanded === seq.id ? null : seq.id)}>
                    {expanded === seq.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                  {seq.status === "draft" && (
                    <Button onClick={() => activate(seq.id)} size="sm" className="rounded-lg bg-[#002FA7] hover:bg-[#1E4FD6] text-white">
                      <Play className="h-3.5 w-3.5 mr-1.5" /> Activate
                    </Button>
                  )}
                  {seq.status === "active" && (
                    <Button onClick={() => setStatus(seq.id, "paused")} size="sm" variant="outline" className="rounded-lg border-[#EAEAE3]">
                      <Pause className="h-3.5 w-3.5 mr-1.5" /> Pause
                    </Button>
                  )}
                  {seq.status === "paused" && (
                    <Button onClick={() => setStatus(seq.id, "active")} size="sm" variant="outline" className="rounded-lg border-[#EAEAE3]">
                      <Play className="h-3.5 w-3.5 mr-1.5" /> Resume
                    </Button>
                  )}
                  <Button onClick={() => deleteSeq(seq.id)} size="icon" variant="ghost" className="rounded-lg h-8 w-8 text-red-500 hover:bg-red-50 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {expanded === seq.id && (
                <div className="border-t border-[#EAEAE3] divide-y divide-[#EAEAE3]">
                  {seq.steps.map((step) => (
                    <div key={step.step_index} className="px-5 py-3 flex items-center gap-4 bg-[#FAFAF7]">
                      <div className="w-7 h-7 rounded-full bg-[#002FA7] text-white flex items-center justify-center text-xs font-bold shrink-0">
                        {step.step_index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium capitalize">{step.tone} tone</div>
                        {step.offer && <div className="text-xs text-[#5C5C66] truncate">Offer: {step.offer}</div>}
                      </div>
                      <div className="text-xs font-mono text-[#9A9AA3] shrink-0">
                        {step.delay_days === 0 ? "Day 0 (immediately)" : `Day +${step.delay_days}`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tighter text-2xl">Create drip sequence</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-mono">Sequence name</Label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="3-step follow-up" className="mt-2 rounded-lg" />
              </div>
              <div>
                <Label className="label-mono">Campaign</Label>
                <Select value={form.campaign_id} onValueChange={(v) => setForm((f) => ({ ...f, campaign_id: v }))}>
                  <SelectTrigger className="mt-2 rounded-lg"><SelectValue placeholder="Select campaign" /></SelectTrigger>
                  <SelectContent>
                    {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="label-mono">Default offer / value prop</Label>
              <Input value={form.offer} onChange={(e) => setForm((f) => ({ ...f, offer: e.target.value }))}
                placeholder="Pipeline platform that books 30 calls/mo" className="mt-2 rounded-lg" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="label-mono">Steps</Label>
                <Button onClick={addStep} size="sm" variant="outline" className="rounded-lg border-[#EAEAE3] text-xs">
                  <Plus className="h-3 w-3 mr-1" /> Add step
                </Button>
              </div>
              <div className="space-y-3">
                {form.steps.map((step, i) => (
                  <div key={i} className="bg-[#FAFAF7] border border-[#EAEAE3] rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-[#002FA7] text-white flex items-center justify-center text-xs font-bold">{i + 1}</div>
                        <span className="text-sm font-medium">Step {i + 1}</span>
                      </div>
                      {form.steps.length > 1 && (
                        <Button onClick={() => removeStep(i)} size="icon" variant="ghost" className="h-6 w-6 rounded text-red-400 hover:text-red-600 hover:bg-red-50">
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="label-mono text-[10px]">Delay (days from start)</Label>
                        <Input type="number" min={0} value={step.delay_days}
                          onChange={(e) => updateStep(i, "delay_days", parseInt(e.target.value) || 0)}
                          className="mt-1.5 rounded-lg h-8 text-sm" />
                      </div>
                      <div>
                        <Label className="label-mono text-[10px]">Tone</Label>
                        <Select value={step.tone} onValueChange={(v) => updateStep(i, "tone", v)}>
                          <SelectTrigger className="mt-1.5 rounded-lg h-8 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label className="label-mono text-[10px]">Step-specific offer (optional)</Label>
                        <Input value={step.offer} onChange={(e) => updateStep(i, "offer", e.target.value)}
                          placeholder="Leave blank to use default offer" className="mt-1.5 rounded-lg h-8 text-sm" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleCreate} disabled={loading} className="rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white">
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating…</> : "Create sequence"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
